import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Doughnut, Bar, getElementAtEvent } from "react-chartjs-2";
import CustomLegend from "../../ChartsGroup/DiamondReportsCharts/_shared/CustomLegend";
import ChartState from "../../ChartsGroup/DiamondReportsCharts/_shared/ChartState";
import { donutOptions, barOptions, donutPctOptions, barPctOptions } from "../../ChartsGroup/DiamondReportsCharts/_shared/chartTheme";
import { chartColors } from "../colors";
import { GLOBAL_REPORT_PERCENT_ONLY } from "../_shared/globalReportSettings";

// Global's Admin_JewelryPriceChartColunmData returns "range"/"total"
// (lowercase, camelCased from C# "Range"/"total"), unlike the Dealer stack's
// "Range"/"total". Same natural-band-order (not count-sorted) logic as
// JewelryPriceComp.js -- small fixed set of bands, no top-N/"Others" needed.
const bandStart = (label) => {
  if (!label || label === "Call For Price") return Infinity;
  const match = String(label).replace(/,/g, "").match(/[\d.]+/);
  return match ? Number(match[0]) : Infinity;
};

const GlobalJewelryPriceComp = ({ data, loading, onSliceClick, onChartTypeChange }) => {
  const { t } = useTranslation();
  const [chartType, setChartType] = useState("bar");
  useEffect(() => { onChartTypeChange?.(chartType); }, [chartType]);
  const chartRef = useRef();

  const rows = useMemo(() => {
    const list = (data || []).filter((item) => Number(item.total) > 0);
    const sorted = [...list].sort((a, b) => bandStart(a.range) - bandStart(b.range));
    const total = sorted.reduce((sum, item) => sum + Number(item.total), 0);
    return sorted.map((item, index) => ({
      label: item.range,
      value: Number(item.total),
      percentage: total > 0 ? ((Number(item.total) / total) * 100).toFixed(2) : 0,
      color: chartColors[index % chartColors.length],
    }));
  }, [data]);

  const chartData = [["Price Band", "Count"], ...rows.map((r) => [`${r.label} (${r.percentage}%)`, r.value])];
  const colors = rows.map((r) => r.color);

  const handleClick = (event) => {
    if (!onSliceClick || !chartRef.current) return;
    const [el] = getElementAtEvent(chartRef.current, event);
    if (!el) return;
    const row = rows[el.index];
    if (row) onSliceClick(row.label);
  };

  const chartJsData = {
    labels: rows.map((r) => `${r.label} (${r.percentage}%)`),
    datasets: [
      {
        // Percent-only (Task 2344): plot shares; otherwise raw counts.
        data: rows.map((r) => (GLOBAL_REPORT_PERCENT_ONLY ? Number(r.percentage) : r.value)),
        backgroundColor: colors,
        borderColor: "#fff",
        borderWidth: chartType === "doughnut" ? 2 : 0,
        borderRadius: chartType === "bar" ? 4 : 0,
      },
    ],
  };

  return (
    <div className="edr-panel mt-4">
      <div className="edr-panel-head">
        <label>
          <b> {t("Jewelry Price Clicks")} </b>
        </label>
        <div className="edr-seg">
          <button className={chartType === "doughnut" ? "edr-on" : ""} onClick={() => setChartType("doughnut")}>{t("Donut")}</button>
          <button className={chartType === "bar" ? "edr-on" : ""} onClick={() => setChartType("bar")}>{t("Bar")}</button>
        </div>
      </div>
      <div className="chart__section" style={{ display: "flex", justifyContent: "center", flexDirection: "column" }}>
        {!loading && rows.length > 0 ? (
          <>
            {chartType === "doughnut" && <CustomLegend chartData={chartData} colorData={colors} />}
            <div className="edr-chartwrap edr-tall">
              {chartType === "doughnut" ? (
                <Doughnut ref={chartRef} data={chartJsData} options={GLOBAL_REPORT_PERCENT_ONLY ? donutPctOptions : donutOptions} onClick={handleClick} />
              ) : (
                <Bar ref={chartRef} data={chartJsData} options={(GLOBAL_REPORT_PERCENT_ONLY ? barPctOptions(false) : barOptions(false))} onClick={handleClick} />
              )}
            </div>
          </>
        ) : (
          <ChartState loading={loading} />
        )}
      </div>
    </div>
  );
};

export default GlobalJewelryPriceComp;
